import { useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import Seo from "@/components/site/Seo";

const SHOTS = [
  { file: "hero-landscape.jpg", label: "Hero — 1200×675", note: "For X / Twitter, LinkedIn, Hacker News, Reddit" },
  { file: "hero-square.jpg", label: "Hero — 1080×1080", note: "For Instagram, X square, WhatsApp status" },
  { file: "dashboard.jpg", label: "Dashboard — 1200×675", note: "For LinkedIn, r/dataisbeautiful" },
  { file: "game.jpg", label: "Higher / Lower — 1200×675", note: "For engagement-heavy posts" },
];

const POSTS = [
  {
    id: "reddit",
    title: "Reddit — r/InternetIsBeautiful",
    where: "https://www.reddit.com/r/InternetIsBeautiful/submit",
    subject: "Universtrend — what the world is actually googling, country by country",
    body: `Spent a few weekends building this because I got obsessed with the idea that "what people google" is the most honest portrait of a culture.

It's an editorial atlas of the most-searched things in 12 countries × 8 categories. Some highlights I didn't expect:

🇮🇳 India: IPL crushes everything (92M searches/month — more than the next 5 combined)
🇰🇷 Korea: BTS still beats Samsung in search volume
🇧🇷 Brazil: PIX (their payment app) is searched more than Neymar
🇯🇵 Japan: "Earthquake" sits between Sushi and Mt. Fuji
🇳🇬 Nigeria: Jollof rice ranks above WhatsApp

There's also a Higher/Lower game where you guess which term is searched more (warning: oddly addictive).

Free, no signup, no tracking: https://universtrend.com

Curious what surprises you in your own country's list — I almost certainly got something wrong.`,
    when: "Tue or Wed, 14:00-18:00 Paris time (peak of the sub)",
  },
  {
    id: "x-thread",
    title: "X / Twitter — 4-tweet thread",
    where: "https://twitter.com/intent/tweet",
    subject: "Attach hero-landscape.jpg to Tweet 1, dashboard.jpg to Tweet 2, game.jpg to Tweet 3",
    body: `TWEET 1 (attach: hero-landscape.jpg):
I got obsessed with a question:

What does the world actually type into Google?

Not the polished trend reports — the real, unfiltered, country-by-country obsessions.

So I built Universtrend 👇

—

TWEET 2 (attach: dashboard.jpg):
12 countries. 8 categories. Hand-picked from raw search volumes.

A few things I didn't expect:

🇮🇳 IPL crushes everything in India (92M/month)
🇰🇷 BTS still outranks Samsung in Korea
🇯🇵 "Earthquake" sits between Sushi and Mt. Fuji
🇳🇬 Jollof rice ranks above WhatsApp

—

TWEET 3 (attach: game.jpg):
And then I made it a game.

Two terms appear. One is googled more than the other. Pick.

I lost 30 minutes the first time.

—

TWEET 4 (no attachment):
Free. No signup. No tracking.

👉 https://universtrend.com

Tell me what surprises you in your country's list.`,
    when: "Same day as Reddit, 30 min after Reddit post",
  },
  {
    id: "hn",
    title: "Hacker News — Show HN",
    where: "https://news.ycombinator.com/submit",
    subject: "Show HN: Universtrend – an editorial index of the world's most-googled things",
    body: `URL field: https://universtrend.com

TEXT field (optional):
Hi HN — I built this over a few weekends because I wanted a quiet, editorial way to look at what the world types into Google.

It covers 12 countries × 8 categories. There's a dashboard (filterable with country/category), per-country and per-category pages, and a Higher/Lower game where you guess which of two terms is searched more.

Stack: FastAPI + MongoDB + React + Tailwind + Recharts. Trends are seeded from public estimates — a real Trends API is on the roadmap.

Open to feedback on the editorial / SEO angle. Curious whether anyone here finds something surprising in their country's list.`,
    when: "Mon-Wed, 15:00-17:00 Paris time (morning HN)",
  },
  {
    id: "linkedin",
    title: "LinkedIn — post + hero image",
    where: "https://www.linkedin.com/feed/",
    subject: "Attach hero-landscape.jpg or dashboard.jpg",
    body: `What does the world Google? A side project I shipped.

For a few weekends, I obsessed over one question: if "you are what you search," then what is each country?

The answer became Universtrend — an editorial atlas of the most-googled things across 12 countries and 8 categories.

A few surprises:
↳ India's most-searched term is "IPL" — bigger than the next five combined
↳ In Korea, BTS still outranks Samsung
↳ "Earthquake" sits between Sushi and Mt. Fuji in Japan
↳ Brazil googles "PIX" more than "Neymar"

Built with React, FastAPI, MongoDB. Editorial / brutalist design.

If you're a brand looking to reach a curious, global, data-loving audience — there's a single tasteful sponsor slot on the homepage starting at $99/week. Self-serve via Stripe, no contracts.

👉 https://universtrend.com

Feedback welcome.`,
    when: "Thursday morning, 09:00-11:00 Paris time (LinkedIn peak)",
  },
  {
    id: "reddit-clippers",
    title: "Reddit r/NewTubers — Clip Radar launch",
    where: "https://www.reddit.com/r/NewTubers/submit",
    subject: "I built a free tool that scans YouTube for videos worth clipping",
    body: `Hey clippers — I built something that might save you time.

The problem: you have to scroll YouTube for hours to find long-form videos with viral clip potential. Which podcast episode is going viral? Which stream is spiking? Which creator allows clips?

The tool: Clip Radar. Scans YouTube every 6h. For each video it computes a "Clip Score" out of 100 based on:
- Duration (30+ min = more clippable)
- Recency (< 30 days = fresh trend)
- View velocity (views/day, not just total)
- Whether the creator officially allows/pays for clips (Rogan, MrBeast, Lex, Kai Cenat, Huberman, etc.)

Filter by 100k / 300k / 1M / 10M views. Free forever for 5 clips/day. Pro tier at $9/mo for unlimited + email alerts.

👉 https://universtrend.com/clips

Would love feedback — what clip-friendly channels should I add? What score component would you tweak?`,
    when: "Weekday 15:00-18:00 Paris (US clipper peak)",
  },
  {
    id: "x-clippers",
    title: "X — Clip Radar launch tweet",
    where: "https://twitter.com/intent/tweet",
    subject: "Attach a screenshot of /clips page showing the score cards",
    body: `Every clipper I know spends 2 hours a day scrolling YouTube looking for videos worth clipping.

I built a tool that does it for you.

Clip Radar scans YouTube every 6h and scores videos on:
📏 Duration
🕐 Recency
📈 View velocity
🎙️ Creator clip-friendliness

5 free clips/day. $9/mo for the full radar.

👉 https://universtrend.com/clips`,
    when: "Same day as Reddit launch, mid-afternoon Paris",
  },
];

function Copy({ text, id }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {
      /* clipboard blocked — noop */
    }
  };
  return (
    <button
      data-testid={`press-copy-${id}`}
      onClick={copy}
      className="overline border border-black px-3 py-1.5 hover:bg-black hover:text-white transition-colors"
    >
      {copied ? "Copied ✓" : "Copy →"}
    </button>
  );
}

export default function PressKit() {
  return (
    <div data-testid="press-kit-page" className="bg-white text-[#0a0a0a]">
      <Seo title="Press Kit — Universtrend" description="Assets and copy for launch." />
      <Header />

      <section className="border-b border-black">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-12">
          <div className="overline text-[#52525b] mb-4">Owner-only · Launch kit</div>
          <h1 className="font-display font-extrabold tracking-tighter leading-[0.95] text-4xl lg:text-6xl">
            Press Kit
          </h1>
          <p className="mt-4 max-w-2xl text-base text-[#52525b]">
            Everything you need to launch — screenshots (right-click to download) and ready-to-post copy for Reddit, X, Hacker News and LinkedIn.
          </p>
        </div>
      </section>

      <section className="border-b border-black">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-12">
          <div className="overline text-[#52525b] mb-6">01 · Screenshots</div>
          <div className="grid grid-cols-1 md:grid-cols-2 border-l border-t border-black">
            {SHOTS.map((s) => (
              <div key={s.file} className="border-r border-b border-black p-5">
                <a
                  href={`/press/${s.file}`}
                  download
                  data-testid={`press-download-${s.file.replace(".jpg", "")}`}
                  className="block border border-black bg-black overflow-hidden hover:opacity-80 transition-opacity"
                >
                  <img
                    src={`/press/${s.file}`}
                    alt={s.label}
                    className="w-full h-auto"
                  />
                </a>
                <div className="mt-4 flex items-baseline justify-between">
                  <div>
                    <div className="font-display font-extrabold text-lg tracking-tight">{s.label}</div>
                    <div className="font-mono text-xs text-[#52525b] mt-1">{s.note}</div>
                  </div>
                  <a
                    href={`/press/${s.file}`}
                    download
                    className="overline border border-black px-3 py-1.5 hover:bg-black hover:text-white transition-colors"
                  >
                    Download →
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-black">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-12">
          <div className="overline text-[#52525b] mb-6">02 · Ready-to-post copy</div>
          <div className="grid grid-cols-1 gap-6">
            {POSTS.map((p) => (
              <article key={p.id} className="border border-black p-6" data-testid={`press-post-${p.id}`}>
                <div className="flex flex-wrap items-baseline justify-between gap-3">
                  <div>
                    <div className="overline text-[#ff3b30]">{p.title}</div>
                    <h3 className="font-display font-extrabold text-2xl tracking-tight mt-1">{p.subject}</h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <Copy id={`${p.id}-title`} text={p.subject} />
                    <Copy id={`${p.id}-body`} text={p.body} />
                    <a
                      href={p.where}
                      target="_blank"
                      rel="noreferrer"
                      className="overline border border-black bg-black text-white px-3 py-1.5 hover:bg-[#ff3b30]"
                    >
                      Open →
                    </a>
                  </div>
                </div>
                <pre className="mt-4 font-mono text-xs leading-relaxed whitespace-pre-wrap bg-[#f4f4f5] border border-black/15 p-4 max-h-[280px] overflow-auto">
{p.body}
                </pre>
                <div className="mt-3 font-mono text-xs text-[#52525b]">
                  Best time: <b>{p.when}</b>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-black">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-12">
          <div className="overline text-[#52525b] mb-6">03 · Launch order</div>
          <ol className="space-y-3 text-base">
            <li><span className="font-mono text-[#52525b] mr-3">D+0</span>Verify AdSense · submit sitemap to Search Console</li>
            <li><span className="font-mono text-[#52525b] mr-3">D+1</span>Reddit r/InternetIsBeautiful, 15:00 Paris</li>
            <li><span className="font-mono text-[#52525b] mr-3">D+1</span>X thread, 30 min after Reddit</li>
            <li><span className="font-mono text-[#52525b] mr-3">D+2</span>Hacker News Show HN, 15:00 Paris</li>
            <li><span className="font-mono text-[#52525b] mr-3">D+3</span>LinkedIn post (target sponsors)</li>
            <li><span className="font-mono text-[#52525b] mr-3">D+5</span>Reddit r/dataisbeautiful (only if Tue Reddit worked)</li>
          </ol>
          <div className="mt-8">
            <Link
              to="/"
              className="hard-shadow inline-block border border-black bg-black text-white px-5 py-2.5 overline"
            >
              ← Back to the Atlas
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
